import React, { ReactElement, useState } from 'react';
import {
  CardContent,
  Typography,
  Card,
  Divider,
  makeStyles,
  Link,
  Tooltip
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import { useSnackbar } from 'notistack';
import { SnackBarConfig } from 'src/utils/SnackBarConfig';

// styles
import './BalancesLocationCard.scss';
// import {DEFAULT_WHITE_COLOR, ROW_EVEN_COLOR} from 'src/style/variable';
import CurrencyFormatter, {
  getFormattedValue
} from '@components/system/CurrencyFormatter/CurrencyFormatter';
import { Trans, useTranslation } from 'react-i18next';
import {
  AppPermissions,
  BALANCE_TYPE_KEYS,
  PaymentTypes,
  StatementNoticeType
} from '@shared/constants/Mapping';
import { getGuarantorBalancesFromMultipleLocations } from '@shared/services/DueInvoiceServices';
import SharePaymentDialog from '../../../Invoices/DueInvoices/SendEmailStatement/SharePaymentDialog';
import HelperUtils from '@shared/utils/HelperUtils';
import CardPayDialog from '@components/system/CardPayDialog/CardPayDialog';
import { getGuarantor } from '@shared/services/PatientsServices';
import Loader from '@components/framework/loader/Loader';
import MakeSettleStatement from '../../../Invoices/DueInvoices/MakeSettleStatement';
import MoneyReceive from '@components/system/MyIcons/MoneyRecieve';
import { APP_PRIMARY_COLOR, DEFAULT_GREY_COLOR } from 'src/style/variable';
import MinGridTable, {
  ColumnDef
} from '@components/system/MinGridTable/MinGridTable';
import TertiaryButton from '@components/system/ButtonField/TertiaryButton';
import { CommunicationService } from 'src/service/CommunicationService';
import { Subscription } from 'rxjs';
import { Permission } from '@shared/services/PermissionService';

interface BalancesLocationCardProps {
  guarantorId: number | string;
  clinicId?: any;
  resetPagesInGrid?(): void;
}

const PatientPayments = makeStyles(() => ({
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  paymentsContainer: { maxHeight: '15.5rem', overflow: 'auto' },
  secondary: {
    '& .MuiListItemText-secondary': {
      fontSize: '0.9rem',
      overflow: 'hidden',
      width: '100%'
    }
  },
  primary: {
    '& .MuiListItemText-primary': {
      fontSize: '1rem',
      overflow: 'hidden',
      width: '100%'
    }
  },
  listItemText: {
    '& .MuiListItemText-multiline': {
      marginTop: 0,
      marginBottom: 0
    }
  },
  locationHeaderStyle: {
    flex: 1,
    textAlign: 'left'
  },
  headerStyle: {
    flex: 1,
    textAlign: 'left'
  },
  location: {
    // fontWeight: 'bold'
    flex: 1
  },
  description: {
    lineHeight: '1.3rem',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
    color: '#343434'
  },
  descriptionStyle: {
    color: '#8f8f90',
    fontSize: '0.9rem',
    lineHeight: '1.3rem'
  },
  bodyStyle: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    justifyContent: 'center'
  },
  balance: {
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-end'
  },
  balanceHeader: {
    flex: 1,
    justifyContent: 'flex-end',
    display: 'flex'
  },
  paidHeader: {
    flexDirection: 'column',
    lineHeight: '1.3rem',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'self-end'
  },
  originalHeader: {
    flexDirection: 'column',
    lineHeight: '1.3rem',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600
  },
  Total: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 1rem 0 0',
    fontWeight: 600
  },
  TotalDue: {
    fontWeight: 'bold'
  },
  links: {
    padding: '0 1rem 1rem 1rem'
  },
  button: {
    fontSize: '1.0rem',
    flex: 1
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBlock: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600,
    paddingBottom: '0.5rem'
  },
  paymentNote: {
    paddingBottom: '1rem'
  },
  cardHeader: {
    '& .MuiCardContent-root': {
      padding: '0'
    }
  },
  amountWrapper: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  originalBalance: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  pay: {
    background: 'rgba(40, 132, 255, 0.1)',
    borderRadius: '8px',
    padding: '0.5rem',
    paddingInline: '0.75rem',
    color: APP_PRIMARY_COLOR,
    overflowWrap: 'break-word',
    cursor: 'pointer'
  },
  loaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  actionsParent: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  actionsChild: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '50%'
  },
  settleLink: {
    whiteSpace: 'nowrap',
    display: 'inline-block',
    textAlign: 'right'
  },
  rightAlignHeader: {
    textAlign: 'right'
  },
  copayAuditInfo: {
    color: DEFAULT_GREY_COLOR,
    fontSize: '0.8rem'
  }
}));

const BalancesLocationCard = (props: BalancesLocationCardProps): any => {
  const { guarantorId, clinicId, resetPagesInGrid } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [emailPopOver, setEmailPopOver] = useState<boolean>(false);
  const [cardPopUpOpen, setCardPopUpOpen] = useState<boolean>(false);
  const [patientDetail, setPatientDetail]: any = useState({});
  const [load, setLoad] = useState<boolean>(false);
  // FIXME: ADD PROPER TYPES
  const acOfClinics: any = useSelector(
    (state: any) => state.clinics.selectedClinicId || []
  );
  const acOfClinicLabels: any = useSelector(
    (state: any) => state.clinics.selectedClinicLabel || []
  );

  const userPermissions: any = useSelector(
    (state: any) => state.auth.userPermissions
  );
  const [compForceUpdate, setCompForceUpdate] = useState(0);

  const classes = PatientPayments();

  const [balancesData, setBalances]: any = useState([]);
  const [totalDue, setTotalDue]: any = useState(0);
  const [
    selectedGuarantorForUpdateNoticeType,
    setSelectedGuarantorForUpdateNoticeType
  ] = useState<any>({});
  const [
    confirmDialogOpenForNoticeType,
    setConfirmDialogOpenForNoticeType
  ] = useState<boolean>(false);
  const [isLoadingBalances, setIsLoadingBalances] = useState<boolean>(true);
  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const selectedClinicId = useSelector(
    (state: any) => state.clinics.selectedClinicId
  );

  const { enqueueSnackbar } = useSnackbar();
  const subscription = React.useRef<Subscription>();

  const fetchGuarantorBalancesFromMultipleLocations = async (): Promise<void> => {
    try {
      const balancesResponse = await getGuarantorBalancesFromMultipleLocations(
        groupId,
        guarantorId,
        (selectedClinicId && selectedClinicId[0]) ?? clinicId,
        true
      );
      if (balancesResponse?.data) {
        const dueRecords = balancesResponse?.data?.dues_list || [];
        const CopayRecords = balancesResponse?.data?.copay_list || [];

        setBalances([...dueRecords, ...CopayRecords]);
        setTotalDue(balancesResponse?.data?.summary.current_due_amount);
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setIsLoadingBalances(false);
    }
  };
  React.useEffect(() => {
    if (guarantorId) {
      fetchGuarantorBalancesFromMultipleLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compForceUpdate]);

  React.useEffect(() => {
    // if there is an update available and no state passed
    subscription.current = CommunicationService.getEvent().subscribe(data => {
      if (
        data.action === 'GUARANTOR_PAYMENT_COMPLETED' ||
        data.action === 'PATIENT_COPAY_ADDED' ||
        data.action === 'PATIENT_COPAY_REMOVED'
      ) {
        fetchGuarantorBalancesFromMultipleLocations();
      }
    });
    return () => {
      subscription.current?.unsubscribe();
    };
    // eslint-disable-next-line
  }, []);

  const handlePopOverItemClick = (item: {
    guarantorId: number;
    clinicId: number;
    firstName: string;
    lastName: string;
    middleName: string;
  }) => {
    setSelectedGuarantorForUpdateNoticeType({
      ...selectedGuarantorForUpdateNoticeType,
      dueNoticeType: StatementNoticeType.STD.key,
      dueNoticeTypeLabel: StatementNoticeType.STD.label,
      id: item?.guarantorId,
      clinic_id: item.clinicId,
      name: HelperUtils.commaSeparatedFullName(
        item?.firstName,
        item?.lastName,
        item?.middleName
      )
    });
    setConfirmDialogOpenForNoticeType(true);
  };

  const fetchGuarantorDetails = async () => {
    if (guarantorId && (clinicId || acOfClinics.length > 0)) {
      setLoad(true);
      getGuarantor(groupId, guarantorId, acOfClinics[0] ?? clinicId)
        .then(async res => {
          if (res && res?.data) {
            setPatientDetail(res?.data?.guarantor || {});
          }
        })
        .catch(err => {
          enqueueSnackbar(
            (err && err.message) || t('PATIENTS.GET_PATIENT_DETAILS_ERROR'),
            SnackBarConfig('e')
          );
        })
        .finally(() => {
          setLoad(false);
        });
    }
  };

  const renderConfirmationForUpdateStatementType = () => {
    if (
      confirmDialogOpenForNoticeType &&
      selectedGuarantorForUpdateNoticeType.dueNoticeType ===
      StatementNoticeType.STD.key
    ) {
      return (
        <MakeSettleStatement
          title={
            <Trans
              i18nKey={
                selectedGuarantorForUpdateNoticeType.dueNoticeType ===
                  StatementNoticeType.STD.key
                  ? 'DUE_INVOICES.SETTLE_STATEMENT_TITLE'
                  : 'DUE_INVOICES.UPDATE_DUE_NOTICE_TYPE_TITLE'
              }
              values={{
                name: selectedGuarantorForUpdateNoticeType.name
              }}
              components={[<strong key={'X'} />]}
            />
          }
          subTitle={
            <Trans
              i18nKey={'DUE_INVOICES.SETTLE_DUE_NOTICE_TYPE_NOTE'}
              values={{
                notice_type:
                  selectedGuarantorForUpdateNoticeType.dueNoticeTypeLabel,
                name: selectedGuarantorForUpdateNoticeType.name
              }}
              components={[<strong key={'X'} />]}
            />
          }
          data-test-id="pcl-payments-settlementPopUp"
          open={confirmDialogOpenForNoticeType}
          onClose={res => {
            setConfirmDialogOpenForNoticeType(false);
            setSelectedGuarantorForUpdateNoticeType({});
            if (res === 'submit') {
              if (typeof fetchGuarantorDetails == 'function') {
                fetchGuarantorDetails();
              }
            }
            setCompForceUpdate(f => f + 1);
          }}
          clinicId={selectedGuarantorForUpdateNoticeType?.clinic_id}
          groupId={groupId}
          guarantorId={selectedGuarantorForUpdateNoticeType.id}
        />
      );
    }
  };

  React.useEffect(() => {
    fetchGuarantorDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guarantorId]);

  const openPayPopOver = (data: any) => {
    dispatch({
      type: 'PAYMENT_TYPE',
      payload: {
        data: PaymentTypes.STATEMENT_PAYMENT
      }
    });
    dispatch({
      type: 'GUARANTOR_PAY_AMOUNT',
      payload: {
        data: data?.guarantor_due?.current_due_amount
      }
    });
    setCardPopUpOpen(true);
  };

  const closeEmailPopOver = () => {
    setEmailPopOver(false);
  };

  const closePayPopOver = () => {
    setCardPopUpOpen(false);
  };
  const renderCardPopUp = (): ReactElement => {
    return (
      <CardPayDialog
        guarantorId={patientDetail?.id}
        clinicId={clinicId ?? acOfClinics[0]}
        clinicGroupId={groupId}
        guarantorName={HelperUtils.commaSeparatedFullName(
          patientDetail?.first_name,
          patientDetail?.last_name,
          patientDetail?.middle_name
        )}
        guarantorDetails={{
          id: patientDetail?.id,
          first_name: patientDetail?.first_name,
          last_name: patientDetail?.last_name,
          middle_name: patientDetail?.middle_name,
          clinic_group_id: patientDetail?.clinic_group_id,
          clinic_id: patientDetail?.clinic_id,
          email: patientDetail?.email,
          address_line_1: patientDetail?.address_line_1,
          zip_code: patientDetail.zip_code
        }}
        open={cardPopUpOpen}
        handleClose={reason => {
          if (
            reason === 'success' &&
            typeof fetchGuarantorDetails == 'function'
          ) {
            fetchGuarantorDetails();
          }
          if (typeof resetPagesInGrid === 'function') {
            resetPagesInGrid();
          }
          closePayPopOver();
        }}
      />
    );
  };

  const balanceLocationColDef: ColumnDef<any>[] = [
    {
      label: t('PATIENT_DETAILS_CARD.LOCATION'),
      render(obj) {
        return (
          <div>
            <p>{obj?.clinic_detail?.name || '-'}</p>
            {obj?.charge_type === PaymentTypes.PATIENT_COPAY_PAYMENT && [
              <p className={classes.descriptionStyle} key={'description'}>
                {t('PATIENT_DETAILS_CARD.COPAY')}
              </p>,
              <p className={classes.copayAuditInfo} key={'name'}>
                {t('COMMON.ADDED_BY', {
                  name: HelperUtils.commaSeparatedFullName(
                    obj?.user?.first_name,
                    obj?.user?.last_name
                  )
                })}
              </p>,
              <p className={classes.copayAuditInfo} key={'created_at'}>
                {t('COMMON.AT', {
                  date: HelperUtils.formatDisplayDate(
                    obj?.created_at,
                    'MM/DD/YYYY'
                  )
                })}
              </p>
            ]}
          </div>
        );
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.DESCRIPTION'),
      render(obj) {
        return (
          <div>
            {obj?.charge_type === PaymentTypes.PATIENT_COPAY_PAYMENT
              ? obj?.description || ''
              : t('PATIENT_DETAILS_CARD.STATEMENT_BALANCE')}
          </div>
        );
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.ORIGINAL_BALANCE'),
      render(obj) {
        const key =
          obj.charge_type === PaymentTypes.PATIENT_COPAY_PAYMENT
            ? BALANCE_TYPE_KEYS.COPAY_BALANCE_KEY
            : BALANCE_TYPE_KEYS.STATEMENT_BALANCE_KEY;
        return (
          <CurrencyFormatter
            value={obj[key] || '0'}
            displayType={'text'}
            thousandSeparator={true}
            prefix={'$'}
            ignoreRound={true}
            useSign={true}
          />
        );
      },
      colValueCellProps: {
        className: 'table-amount-col'
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    },
    {
      label: t('PATIENT_DETAILS_CARD.PAID_BALANCE') as string,
      render(obj) {
        const anotherKey =
          obj.charge_type === PaymentTypes.PATIENT_COPAY_PAYMENT
            ? BALANCE_TYPE_KEYS.COPAY_BALANCE_PAID
            : BALANCE_TYPE_KEYS.STATEMENT_BALANCE_PAID;
        return (
          <div className="settle_wrapper">
            <p className="settle-view table-amount-col">
              <CurrencyFormatter
                value={obj[anotherKey] || '0'}
                displayType={'text'}
                thousandSeparator={true}
                prefix={'$'}
                ignoreRound={true}
                useSign={true}
              />
            </p>
            {obj.is_settled === true
              ? t('DUE_INVOICES.PREVIOUS_IMPORTED.SETTLED')
              : !obj?.charge_type &&
              Permission.hasStatementPermissions(
                userPermissions,
                AppPermissions.STATEMENT.LIST,
                groupId
              ) && (
                <Link
                  data-test-id="pcl-payments-settleStatementHyperLink"
                  className={`link-style' ${classes.settleLink}`}
                  key={'settle_this_statement'}
                  component="button"
                  onClick={() => {
                    handlePopOverItemClick({
                      guarantorId: obj?.guarantor_id,
                      clinicId: obj?.clinic_id,
                      firstName: patientDetail?.first_name,
                      middleName: patientDetail?.middle_name,
                      lastName: patientDetail?.last_name
                    });
                  }}>
                  {t('DUE_INVOICES.UPDATE_NOTICE_TYPE.STD')}
                </Link>
              )}
          </div>
        );
      },
      colHeaderCellProps: {
        className: classes.rightAlignHeader
      }
    }
  ];

  const renderBalanceSection = (): ReactElement => {
    return (
      <div>
        <MinGridTable
          isLoading={isLoadingBalances}
          colDefs={balanceLocationColDef}
          rows={balancesData}
          noDataText={t('PAYMENT_HISTORY.NO_BALANCE_LOCATION_FOUND')}
        />
        <Divider />
        <div className={classes.Total}>
          <Typography
            className=" payments-section-view input-header section section-padding"
            component={'div'}>
            {t('PATIENT_DETAILS_CARD.TOTAL_DUE')}
          </Typography>
          <div className={classes.TotalDue}>
            <CurrencyFormatter
              value={totalDue || '0'}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
              ignoreRound={true}
              useSign={true}
            />
          </div>
        </div>
        <div className={classes.links}>
          {load ? (
            <div className={classes.loaderStyle}>{<Loader />}</div>
          ) : (
            <div className={classes.actionsParent}>
              {(Permission.hasStatementPermissions(
                userPermissions,
                AppPermissions.STATEMENT.PAY,
                groupId
              ) && Permission.hasPaymentHistoryPermissions(
                userPermissions,
                AppPermissions.PAYMENT_HISTORY.LIST,
                groupId
              )) && (
                  <div className={classes.actionsChild}>
                    <Tooltip
                      title={t('DUE_INVOICES.SHARE_PAYMENT_LINK').toString()}>
                      <TertiaryButton
                        data-test-id="pcl-payments-sharePaymentLinkInBalancesLocationCard"
                        key={'share_payment_link'}
                        onClick={(e: any): void => {
                          setEmailPopOver(true);
                        }}
                      // startIcon={<MoneyReceive />}
                      >
                        {t('DUE_INVOICES.SHARE_PAYMENT_LINK')}
                      </TertiaryButton>
                    </Tooltip>
                  </div>
                )}
              {(Permission.hasStatementPermissions(
                userPermissions,
                AppPermissions.STATEMENT.PAY,
                groupId
              ) && Permission.hasPaymentHistoryPermissions(
                userPermissions,
                AppPermissions.PAYMENT_HISTORY.LIST,
                groupId
              )) && (
                  <div className={classes.actionsChild}>
                    <Tooltip title={t('DUE_INVOICES.PAY').toString()}>
                      <TertiaryButton
                        data-test-id="pcl-payments-takePaymentLinkInBalancesLocationCard"
                        key={'take_payment'}
                        onClick={
                          totalDue == 0
                            ? () => {
                              enqueueSnackbar(
                                t('GUARANTOR_ERROR_NO_DUE'),
                                SnackBarConfig('e')
                              );
                            }
                            : (e: any): void => {
                              openPayPopOver(patientDetail);
                              e.stopPropagation();
                            }
                        }
                        startIcon={<MoneyReceive />}>
                        {t('DUE_INVOICES.PAY')}
                      </TertiaryButton>
                    </Tooltip>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      <Typography
        data-test-id="pcl-payments-guarantorBalancesTitle"
        className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        {t('PATIENT_DETAILS_CARD.BALANCES')}
      </Typography>
      <Typography
        className={[classes.paymentNote].join(' ')}
        data-test-id="pcl-payments-balanceSection">
        {t('PATIENT_DETAILS_CARD.BALANCE_NOTE')}
      </Typography>
      <Card className={classes.cardHeader}>
        <CardContent>
          <div className="balances-location-card-wrapper">
            <div className="content-view">
              <div className="payment-details-content-views">
                {renderBalanceSection()}
              </div>
            </div>
          </div>
          {confirmDialogOpenForNoticeType &&
            renderConfirmationForUpdateStatementType()}
          {cardPopUpOpen && renderCardPopUp()}
          {emailPopOver && (
            <SharePaymentDialog
              open={emailPopOver}
              onClose={closeEmailPopOver}
              title={
                <Trans
                  i18nKey={'DUE_INVOICES.EMAIL_DIALOG.SHARE_PAYMENT_HEADING'}
                  values={{
                    guarantorName: HelperUtils.commaSeparatedFullName(
                      patientDetail.guarantor_first_name ||
                      patientDetail.first_name,
                      patientDetail.guarantor_last_name ||
                      patientDetail.last_name
                    )
                  }}
                  components={[<strong key={'X'} />]}
                />
              }
              subtitle={
                <Trans
                  i18nKey={'DUE_INVOICES.EMAIL_DIALOG.SHARE_PAYMENT_SUBTITLE'}
                  values={{
                    amount: getFormattedValue(
                      patientDetail.guarantor_due?.current_due_amount,
                      true
                    ),
                    clinicName: acOfClinicLabels[0],
                    guarantorName: HelperUtils.commaSeparatedFullName(
                      patientDetail.guarantor_first_name ||
                      patientDetail.first_name,
                      patientDetail.guarantor_last_name ||
                      patientDetail.last_name
                    )
                  }}
                  components={[<strong key={'X'} />]}
                />
              }
              groupId={groupId}
              selectedGuarantor={{
                ...patientDetail,
                balance_amount: patientDetail.guarantor_due?.current_due_amount
              }}
              clinicId={clinicId ?? acOfClinics[0]}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default BalancesLocationCard;